// RX 신규 표면 계측 — conversion_events 한 곳(최종설계안_RX §7·§11 G14).
//
// 서버(/api/events/cta)는 event_type 을 자유 문자열로 받는다(40자). 로컬·프리뷰는 telemetry-gate 가 no-op.
// ⚠️ 슬롯 UI 와 «같은 커밋» 에 둔다 — 계측 없는 슬롯은 «역사상 0건» 이 재발한다(P0-C).
// ⛔ 호출부는 await 하지 않는다(클릭은 navigate 직전 → sendBeacon/keepalive).

export type RxEvent = 'ad_slot_view' | 'ad_slot_click' | 'schedule_band_click' | 'region_select_apply' | 'share_click' | 'virtual_pageview'
  /** nav_region1 v2 — 첫 방문 지역 제안. cta_name = 제안 코드 · category = shown|accepted|dismissed · target = 그 쪽 경로(수락이면 목적지). */
  | 'region_suggest';

export interface RxPayload {
  /** 서버 route 가 받는 열 이름 그대로(target → referrer_source). buildRxPayload 가 채운다. */
  referrer_source?: string | null;
  event_type: RxEvent;
  /** ad_slot_*: slot id · schedule_band_click: variant · region_select_apply: 선택 코드 · share_click: method(kakao|copy|native). */
  cta_name: string;
  /** ad_slot_*: creative id · schedule_band_click: 행 종류(pin|row) · region_select_apply: `{variant}:{result}`(result = navigated|filtered|noop)
   *  · virtual_pageview: 적용 출처(param|memory). */
  category?: string | null;
  /** nav_region1 — 적용 목적지(region_select_apply 의 target URL). conversion_events 에 전용 열이 없어 referrer_source 열(80자)에 싣는다
   *  (스키마 변경 0 · 이 두 이벤트에서만 «목적지» 의미 — 판독 시 event_type 으로 거른다). */
  target?: string | null;
  page_path?: string | null;
  visitor_id?: string | null;
  gate_position?: number | null;
}

/** 순수 — 페이로드 정규화(길이 상한은 서버 route 와 같다). */
export function buildRxPayload(p: RxPayload): RxPayload | null {
  const event_type = p.event_type;
  const cta_name = String(p.cta_name ?? '').slice(0, 80);
  if (!event_type || !cta_name) return null;
  return {
    event_type,
    cta_name,
    category: p.category ? String(p.category).slice(0, 40) : null,
    page_path: p.page_path ? String(p.page_path).slice(0, 200) : null,
    visitor_id: p.visitor_id ? String(p.visitor_id).slice(0, 80) : null,
    gate_position: Number.isFinite(Number(p.gate_position)) ? Number(p.gate_position) : null,
    ...(p.target ? { referrer_source: String(p.target).slice(0, 80) } : {}),
  };
}

/** 클라이언트 전용. 실패는 삼킨다(UI 를 막지 않는다). */
export function trackRx(event_type: RxEvent, cta_name: string, category?: string | null, opts?: { target?: string | null }): void {
  if (typeof window === 'undefined') return;
  try {
    const body = buildRxPayload({
      event_type, cta_name, category, target: opts?.target ?? null,
      page_path: window.location.pathname,
      visitor_id: readVid(),
    });
    if (!body) return;
    const json = JSON.stringify(body);
    if (navigator.sendBeacon && navigator.sendBeacon('/api/events/cta', new Blob([json], { type: 'text/plain' }))) return;
    void fetch('/api/events/cta', { method: 'POST', body: json, keepalive: true, headers: { 'Content-Type': 'text/plain' } }).catch(() => {});
  } catch { /* silent */ }
}

function readVid(): string | null {
  try {
    const m = /(?:^|;\s*)kd_vid=([^;]+)/.exec(document.cookie);
    return m ? decodeURIComponent(m[1]) : null;
  } catch { return null; }
}
